import React, { useState, useMemo, useCallback } from 'react';
import {
  ChevronLeft, Code, Plus, Trash2, Copy, CheckCircle,
  Download, Box, Link, ChevronDown, ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProfileVisualizer from '@/components/ProfileVisualizer';
// 3D viewer loaded at runtime only when user clicks "Show 3D"

const CycleGeneratorPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('g71');
  const [copied, setCopied] = useState(false);

  const tabs = [
    { id: 'g71', label: 'G71/G70', desc: 'OD/ID Turn' },
    { id: 'g72', label: 'G72', desc: 'Face Rough' },
    { id: 'g75', label: 'G75', desc: 'Grooving' },
    { id: 'g76', label: 'G76', desc: 'Threading' },
    { id: 'g81', label: 'G81-G84', desc: 'Drill/Tap' },
    { id: 'chain', label: 'Chain', desc: 'Multi-Cycle' },
  ];

  const copyCode = (code) => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const downloadCode = (code, filename) => {
    const b = new Blob([code], { type: 'text/plain' }); const u = URL.createObjectURL(b);
    const a = document.createElement('a'); a.href = u; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u);
  };

  return (
    <div className="min-h-screen bg-industrial-bg pb-20">
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" /><span className="text-sm">Back</span>
          </button>
          <h2 className="font-heading font-bold text-orange-400 flex items-center gap-2">
            <Code className="w-5 h-5" />CYCLE GENERATOR
          </h2>
          <div className="w-16" />
        </div>
      </div>
      <div className="px-4 pt-3">
        <div className="flex gap-1 bg-industrial-surface border border-industrial-border rounded-lg p-1.5 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex-shrink-0 px-3 py-2 rounded-md text-center transition-all ${activeTab === t.id ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-industrial-text-secondary hover:text-industrial-text'}`}
              data-testid={`tab-${t.id}`}>
              <div className="text-xs font-bold">{t.label}</div>
              <div className="text-[10px] opacity-70">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="p-4">
        {activeTab === 'g71' && <G71Generator onCopy={copyCode} onDownload={downloadCode} copied={copied} />}
        {activeTab === 'g72' && <G72Generator onCopy={copyCode} onDownload={downloadCode} copied={copied} />}
        {activeTab === 'g75' && <G75Generator onCopy={copyCode} onDownload={downloadCode} copied={copied} />}
        {activeTab === 'g76' && <G76Generator onCopy={copyCode} onDownload={downloadCode} copied={copied} />}
        {activeTab === 'g81' && <DrillTapGenerator onCopy={copyCode} onDownload={downloadCode} copied={copied} />}
        {activeTab === 'chain' && <CycleChainBuilder onCopy={copyCode} onDownload={downloadCode} copied={copied} />}
      </div>
    </div>
  );
};

// ============ G71/G70 OD & ID Rough/Finish Generator ============
const G71Generator = ({ onCopy, onDownload, copied }) => {
  const [mode, setMode] = useState('od'); // 'od' or 'id'
  const [stockDia, setStockDia] = useState(2.0);
  const [boreDia, setBoreDia] = useState(0.0); // Pre-drilled bore for ID
  const [depthOfCut, setDepthOfCut] = useState(0.080);
  const [retract, setRetract] = useState(0.030);
  const [finishX, setFinishX] = useState(0.020);
  const [finishZ, setFinishZ] = useState(0.005);
  const [roughFeed, setRoughFeed] = useState(0.010);
  const [finishFeed, setFinishFeed] = useState(0.004);
  const [sfm, setSfm] = useState(400);
  const [maxRpm, setMaxRpm] = useState(3000);
  const [stages, setStages] = useState([
    { startDia: 1.500, endDia: 1.500, length: 1.000, entryR: 0, exitR: 0 },
    { startDia: 1.000, endDia: 1.000, length: 0.750, entryR: 0.031, exitR: 0 },
    { startDia: 1.500, endDia: 1.500, length: 0.500, entryR: 0, exitR: 0 },
  ]);

  const addStage = () => setStages([...stages, { startDia: stages[stages.length-1]?.endDia || 1.0, endDia: 1.0, length: 0.500, entryR: 0, exitR: 0 }]);
  const removeStage = (i) => setStages(stages.filter((_, idx) => idx !== i));
  const updateStage = (i, field, val) => { const s = [...stages]; s[i] = { ...s[i], [field]: parseFloat(val) || 0 }; setStages(s); };

  const gcode = useMemo(() => {
    if (stages.length === 0) return '';
    const isID = mode === 'id';
    const lines = [];
    const startX = isID ? (boreDia > 0 ? (boreDia - 0.2).toFixed(3) : '0.000') : (stockDia + 0.2).toFixed(3);
    const toolNum = isID ? 'T0202' : 'T0101';
    const toolDesc = isID ? 'BORING BAR' : 'OD TURNING TOOL';
    const cycleDesc = isID ? 'ID BORING' : 'OD ROUGH & FINISH';

    lines.push('%');
    lines.push(`O0071 (G71/G70 ${cycleDesc} CYCLE)`);
    lines.push('(UNITS: INCH)');
    lines.push(`(STOCK DIA: ${stockDia.toFixed(3)}"${isID ? `, PRE-BORE: ${boreDia.toFixed(3)}"` : ''})`);
    lines.push('');
    lines.push('G20 G18 G40 G80 G99       (INCH, ZX PLANE, CANCEL COMP/CYCLES, FEED/REV)');
    lines.push(`G50 S${maxRpm}                   (MAX RPM LIMIT)`);
    lines.push(`${toolNum} M08                  (${toolDesc}, COOLANT ON)`);
    lines.push(`G96 S${sfm} M03                (CSS ${sfm} SFM, SPINDLE CW)`);
    lines.push(`G00 X${startX} Z0.100       (RAPID TO START POSITION)`);
    lines.push('');
    lines.push(`(--- G71 ${cycleDesc} CYCLE ---)`);
    lines.push('');
    lines.push('(--- FINISH PROFILE DEFINITION ---)');

    let zPos = 0;
    let nBlock = 100;
    const profileLines = [];
    
    profileLines.push(`N${nBlock} G00 X${stages[0].startDia.toFixed(4)}        (PROFILE START)`);
    nBlock += 10;
    profileLines.push(`N${nBlock} G01 Z0.0000 F${finishFeed.toFixed(4)}  (FEED TO FACE)`);

    for (let i = 0; i < stages.length; i++) {
      const s = stages[i];
      const prevDia = i > 0 ? stages[i-1].endDia : s.startDia;
      if (i > 0 && Math.abs(prevDia - s.startDia) > 0.001) {
        nBlock += 10;
        if (s.entryR > 0) {
          const midZ = zPos - s.entryR;
          const arcDir = isID ? (s.startDia > prevDia ? 'G03' : 'G02') : (s.startDia < prevDia ? 'G03' : 'G02');
          profileLines.push(`N${nBlock} ${arcDir} X${s.startDia.toFixed(4)} Z${midZ.toFixed(4)} R${s.entryR.toFixed(4)}  (RADIUS R${s.entryR.toFixed(3)}")`);
          zPos = midZ;
        } else {
          profileLines.push(`N${nBlock} G01 X${s.startDia.toFixed(4)}        (STEP TO DIA ${s.startDia.toFixed(3)}")`);
        }
      }
      const endZ = zPos - s.length;
      nBlock += 10;
      if (Math.abs(s.startDia - s.endDia) > 0.001) {
        profileLines.push(`N${nBlock} G01 X${s.endDia.toFixed(4)} Z${endZ.toFixed(4)}  (TAPER ${s.length.toFixed(3)}")`);
      } else {
        profileLines.push(`N${nBlock} G01 Z${endZ.toFixed(4)}              (${isID ? 'BORE' : 'TURN'} ${s.length.toFixed(3)}" AT DIA ${s.startDia.toFixed(3)}")`);
      }
      zPos = endZ;
    }
    nBlock += 10;
    const retractX = isID ? (boreDia > 0 ? (boreDia - 0.100).toFixed(4) : '0.0000') : (stockDia + 0.050).toFixed(4);
    profileLines.push(`N${nBlock} G01 X${retractX}        (RETRACT CLEAR)`);
    const lastN = nBlock;

    // G71 with correct U sign for ID (negative = boring inward toward center)
    const uSign = isID ? `-${finishX.toFixed(3)}` : finishX.toFixed(3);
    lines.push(`G71 U${depthOfCut.toFixed(3)} R${retract.toFixed(3)}        (DOC ${depthOfCut.toFixed(3)}" RAD, RETRACT ${retract.toFixed(3)}")`);
    lines.push(`G71 P100 Q${lastN} U${uSign} W${finishZ.toFixed(3)} F${roughFeed.toFixed(4)}`);
    lines.push(`(FINISH ALLOW: X=${uSign}" DIA, Z=${finishZ.toFixed(3)}")`);
    lines.push('');
    profileLines.forEach(l => lines.push(l));
    lines.push('');
    lines.push('(--- G70 FINISH PASS ---)');
    lines.push(`G70 P100 Q${lastN}              (FINISH PASS)`);
    lines.push('');
    const safeX = isID ? '0.000' : (stockDia + 0.500).toFixed(3);
    lines.push(`G00 X${safeX} Z1.000           (RETRACT SAFE)`);
    lines.push('M09                        (COOLANT OFF)');
    lines.push('G28 U0 W0                  (RETURN HOME)');
    lines.push('M01                        (OPTIONAL STOP)');
    lines.push('M30                        (PROGRAM END)');
    lines.push('%');
    return lines.join('\n');
  }, [mode, stages, stockDia, boreDia, depthOfCut, retract, finishX, finishZ, roughFeed, finishFeed, sfm, maxRpm]);

  return (
    <div className="space-y-4">
      {/* OD / ID Toggle */}
      <div className="grid grid-cols-2 gap-2">
        {[{v:'od',l:'OD Turning',d:'External profile'},{v:'id',l:'ID Boring',d:'Internal bore profile'}].map(m => (
          <button key={m.v} onClick={() => setMode(m.v)}
            className={`py-2.5 rounded-lg border text-xs font-medium transition-all ${mode === m.v ? 'bg-orange-500/20 border-orange-400 text-orange-400' : 'bg-industrial-surface border-industrial-border text-industrial-text-secondary'}`}
            data-testid={`mode-${m.v}`}>
            <div className="font-bold">{m.l}</div>
            <div className="text-[10px] opacity-70">{m.d}</div>
          </button>
        ))}
      </div>

      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-3">
        <h3 className="text-sm font-heading font-bold text-orange-400">G71/G70 {mode === 'id' ? 'ID Boring' : 'OD Turning'} Parameters</h3>
        <div className="grid grid-cols-3 gap-2">
          <InputField label="Stock OD" value={stockDia} onChange={setStockDia} suffix='"' testId="stock-dia" />
          {mode === 'id' && <InputField label="Pre-Bore ID" value={boreDia} onChange={setBoreDia} suffix='"' testId="bore-dia" />}
          <InputField label="DOC (radial)" value={depthOfCut} onChange={setDepthOfCut} suffix='"' testId="doc" />
          <InputField label="Retract" value={retract} onChange={setRetract} suffix='"' testId="retract" />
          <InputField label="Finish X (dia)" value={finishX} onChange={setFinishX} suffix='"' testId="fin-x" />
          <InputField label="Finish Z" value={finishZ} onChange={setFinishZ} suffix='"' testId="fin-z" />
          <InputField label="Rough Feed" value={roughFeed} onChange={setRoughFeed} suffix="IPR" testId="rough-feed" />
          <InputField label="Finish Feed" value={finishFeed} onChange={setFinishFeed} suffix="IPR" testId="fin-feed" />
          <InputField label="CSS (SFM)" value={sfm} onChange={setSfm} suffix="" testId="sfm" />
          <InputField label="Max RPM" value={maxRpm} onChange={setMaxRpm} suffix="" testId="max-rpm" />
        </div>
      </div>
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-heading font-bold text-orange-400">{mode === 'id' ? 'Bore Profile (Face → Deep)' : 'OD Profile (Face → Chuck)'}</h3>
          <button onClick={addStage} className="text-xs bg-orange-500/20 text-orange-400 px-3 py-1.5 rounded-lg border border-orange-500/30 flex items-center gap-1" data-testid="add-stage"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {stages.map((s, i) => (
          <div key={i} className="bg-industrial-bg border border-industrial-border rounded-lg p-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-industrial-text-secondary font-medium">Stage {i + 1}</span>
              {stages.length > 1 && <button onClick={() => removeStage(i)} className="text-red-400 hover:text-red-300" data-testid={`remove-stage-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              <InputField label={mode === 'id' ? 'Start ID' : 'Start OD'} value={s.startDia} onChange={v => updateStage(i, 'startDia', v)} mini testId={`stage-${i}-start`} />
              <InputField label={mode === 'id' ? 'End ID' : 'End OD'} value={s.endDia} onChange={v => updateStage(i, 'endDia', v)} mini testId={`stage-${i}-end`} />
              <InputField label="Length" value={s.length} onChange={v => updateStage(i, 'length', v)} mini testId={`stage-${i}-len`} />
              <InputField label="Entry R" value={s.entryR} onChange={v => updateStage(i, 'entryR', v)} mini testId={`stage-${i}-er`} />
              <InputField label="Exit R" value={s.exitR} onChange={v => updateStage(i, 'exitR', v)} mini testId={`stage-${i}-xr`} />
            </div>
          </div>
        ))}
      </div>
      <GCodeOutput code={gcode} onCopy={onCopy} onDownload={onDownload} copied={copied} filename={`G71_${mode}_program.nc`} stockDia={stockDia} stages={stages} cycleType="g71" depthOfCut={depthOfCut} finishAllowX={finishX} />
    </div>
  );
};

// ============ G72 Face Roughing Generator ============
const G72Generator = ({ onCopy, onDownload, copied }) => {
  const [stockDia, setStockDia] = useState(3.0);
  const [finalDia, setFinalDia] = useState(1.0);
  const [faceDepth, setFaceDepth] = useState(0.500);
  const [depthOfCut, setDepthOfCut] = useState(0.080);
  const [retract, setRetract] = useState(0.030);
  const [finishX, setFinishX] = useState(0.020);
  const [finishZ, setFinishZ] = useState(0.005);
  const [feed, setFeed] = useState(0.010);
  const [sfm, setSfm] = useState(400);
  const [maxRpm, setMaxRpm] = useState(3000);

  const gcode = useMemo(() => {
    const lines = [];
    lines.push('%'); lines.push('O0072 (G72 FACE ROUGHING CYCLE)'); lines.push('(UNITS: INCH)');
    lines.push(`(STOCK DIA: ${stockDia.toFixed(3)}", FACE DEPTH: ${faceDepth.toFixed(3)}")`);
    lines.push(''); lines.push('G20 G18 G40 G80 G99       (INCH, ZX PLANE, CANCEL)');
    lines.push(`G50 S${maxRpm}                   (MAX RPM)`);
    lines.push('T0101 M08                  (TOOL 1, COOLANT ON)');
    lines.push(`G96 S${sfm} M03                (CSS ${sfm} SFM)`);
    lines.push(`G00 X${(stockDia + 0.200).toFixed(3)} Z0.100  (RAPID TO START)`);
    lines.push(''); lines.push('(--- G72 FACE ROUGHING CYCLE ---)');
    lines.push(`G72 W${depthOfCut.toFixed(3)} R${retract.toFixed(3)}        (DOC ${depthOfCut.toFixed(3)}" IN Z, RETRACT ${retract.toFixed(3)}")`);
    lines.push(`G72 P100 Q130 U${finishX.toFixed(3)} W${finishZ.toFixed(3)} F${feed.toFixed(4)}`);
    lines.push(''); lines.push('(--- PROFILE DEFINITION ---)');
    lines.push(`N100 G00 Z${(-faceDepth).toFixed(4)}           (PROFILE START - DEEPEST Z)`);
    lines.push(`N110 G01 X${finalDia.toFixed(4)} F0.0040   (FEED TO FINAL DIA)`);
    lines.push(`N120 G01 Z0.0000              (FEED TO FACE)`);
    lines.push(`N130 G01 X${(stockDia + 0.050).toFixed(4)}       (RETRACT TO STOCK OD)`);
    lines.push(''); lines.push('G70 P100 Q130              (FINISH FACE PROFILE)');
    lines.push(''); lines.push(`G00 X${(stockDia + 0.500).toFixed(3)} Z1.000  (RETRACT SAFE)`);
    lines.push('M09                        (COOLANT OFF)'); lines.push('G28 U0 W0                  (HOME)');
    lines.push('M30                        (END)'); lines.push('%');
    return lines.join('\n');
  }, [stockDia, finalDia, faceDepth, depthOfCut, retract, finishX, finishZ, feed, sfm, maxRpm]);

  return (
    <div className="space-y-4">
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-3">
        <h3 className="text-sm font-heading font-bold text-orange-400">G72 Face Roughing Parameters</h3>
        <div className="grid grid-cols-3 gap-2">
          <InputField label="Stock Dia" value={stockDia} onChange={setStockDia} suffix='"' testId="g72-stock" />
          <InputField label="Final Dia" value={finalDia} onChange={setFinalDia} suffix='"' testId="g72-final" />
          <InputField label="Face Depth" value={faceDepth} onChange={setFaceDepth} suffix='"' testId="g72-depth" />
          <InputField label="DOC (Z)" value={depthOfCut} onChange={setDepthOfCut} suffix='"' testId="g72-doc" />
          <InputField label="Retract" value={retract} onChange={setRetract} suffix='"' testId="g72-retract" />
          <InputField label="Feed" value={feed} onChange={setFeed} suffix="IPR" testId="g72-feed" />
          <InputField label="Finish X" value={finishX} onChange={setFinishX} suffix='"' testId="g72-finx" />
          <InputField label="Finish Z" value={finishZ} onChange={setFinishZ} suffix='"' testId="g72-finz" />
          <InputField label="SFM" value={sfm} onChange={setSfm} suffix="" testId="g72-sfm" />
        </div>
      </div>
      <GCodeOutput code={gcode} onCopy={onCopy} onDownload={onDownload} copied={copied} filename="G72_face.nc" stockDia={stockDia} cycleType="g72" facingParams={{ finalDia, faceDepth }} />
    </div>
  );
};

// ============ G75 OD & ID Grooving Generator ============
const G75Generator = ({ onCopy, onDownload, copied }) => {
  const [grMode, setGrMode] = useState('od'); // 'od' or 'id'
  const [stockDia, setStockDia] = useState(2.0);
  const [grooveDia, setGrooveDia] = useState(1.500); // Bottom of groove (OD=smaller, ID=larger)
  const [grooveWidth, setGrooveWidth] = useState(0.125);
  const [grooveZ, setGrooveZ] = useState(-0.500); // Z position of groove center
  const [peckDepth, setPeckDepth] = useState(0.030);
  const [retract, setRetract] = useState(0.010);
  const [feed, setFeed] = useState(0.003);
  const [sfm, setSfm] = useState(300);
  const [maxRpm, setMaxRpm] = useState(2500);
  const [dwellMs, setDwellMs] = useState(300);
  const [grooveCount, setGrooveCount] = useState(1);
  const [grooveSpacing, setGrooveSpacing] = useState(0.500);

  const gcode = useMemo(() => {
    const isID = grMode === 'id';
    const lines = [];
    const toolNum = isID ? 'T0404' : 'T0303';
    const toolDesc = isID ? 'ID GROOVING TOOL' : 'OD GROOVING TOOL';
    const startX = isID ? (grooveDia - 0.200).toFixed(3) : (stockDia + 0.200).toFixed(3);
    const peckMicrons = Math.round(peckDepth * 1000);
    
    lines.push('%');
    lines.push(`O0075 (G75 ${isID ? 'ID' : 'OD'} GROOVING CYCLE)`);
    lines.push('(UNITS: INCH)');
    lines.push(`(${isID ? 'ID' : 'OD'} GROOVE: DIA ${grooveDia.toFixed(3)}", WIDTH ${grooveWidth.toFixed(3)}", AT Z${grooveZ.toFixed(3)}")`);
    lines.push(`(GROOVE COUNT: ${grooveCount}${grooveCount > 1 ? `, SPACING: ${grooveSpacing.toFixed(3)}"` : ''})`);
    lines.push('');
    lines.push('G20 G18 G40 G80 G99       (INCH, ZX PLANE, CANCEL)');
    lines.push(`G50 S${maxRpm}                   (MAX RPM LIMIT)`);
    lines.push(`${toolNum} M08                  (${toolDesc}, COOLANT ON)`);
    lines.push(`G96 S${sfm} M03                (CSS ${sfm} SFM, SPINDLE CW)`);
    lines.push(`G00 X${startX} Z0.200       (RAPID TO SAFE POSITION)`);
    lines.push('');

    for (let g = 0; g < grooveCount; g++) {
      const zCenter = grooveZ - (g * grooveSpacing);
      const zStart = zCenter + grooveWidth / 2;
      const zEnd = zCenter - grooveWidth / 2;
      
      lines.push(`(--- GROOVE ${g + 1} AT Z${zCenter.toFixed(3)}" ---)`);
      lines.push(`G00 Z${zStart.toFixed(4)}              (RAPID TO GROOVE START Z)`);
      
      if (grooveWidth <= 0.160) {
        // Single plunge groove (insert width covers the groove)
        lines.push(`G00 X${(isID ? (grooveDia + 0.050) : (stockDia - 0.050)).toFixed(3)}  (APPROACH)`);
        lines.push(`G75 R${retract.toFixed(3)}`);
        lines.push(`G75 X${grooveDia.toFixed(4)} Z${zEnd.toFixed(4)} P${peckMicrons} Q0 F${feed.toFixed(4)}`);
        lines.push(`(PECK: ${peckDepth.toFixed(3)}" PER PASS, SINGLE PLUNGE GROOVE)`);
      } else {
        // Multi-pass groove (wider than insert, use Z stepping)
        const zStepMicrons = Math.round(grooveWidth * 500); // Half groove width steps
        lines.push(`G00 X${(isID ? (grooveDia + 0.050) : (stockDia - 0.050)).toFixed(3)}  (APPROACH)`);
        lines.push(`G75 R${retract.toFixed(3)}`);
        lines.push(`G75 X${grooveDia.toFixed(4)} Z${zEnd.toFixed(4)} P${peckMicrons} Q${zStepMicrons} F${feed.toFixed(4)}`);
        lines.push(`(PECK X: ${peckDepth.toFixed(3)}", STEP Z: ${(grooveWidth / 2).toFixed(3)}")`);
      }
      
      // Dwell at bottom
      lines.push(`G04 P${dwellMs}                   (DWELL ${(dwellMs/1000).toFixed(1)}s AT GROOVE BOTTOM)`);
      lines.push(`G00 X${startX}              (RETRACT CLEAR)`);
      lines.push('');
    }

    lines.push(`G00 X${startX} Z1.000       (RETRACT SAFE)`);
    lines.push('M09                        (COOLANT OFF)');
    lines.push('G28 U0 W0                  (HOME)');
    lines.push('M01                        (OPTIONAL STOP)');
    lines.push('M30                        (END)');
    lines.push('%');
    return lines.join('\n');
  }, [grMode, stockDia, grooveDia, grooveWidth, grooveZ, peckDepth, retract, feed, sfm, maxRpm, dwellMs, grooveCount, grooveSpacing]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {[{v:'od',l:'OD Grooving',d:'External grooves'},{v:'id',l:'ID Grooving',d:'Internal grooves'}].map(m => (
          <button key={m.v} onClick={() => setGrMode(m.v)}
            className={`py-2.5 rounded-lg border text-xs font-medium transition-all ${grMode === m.v ? 'bg-orange-500/20 border-orange-400 text-orange-400' : 'bg-industrial-surface border-industrial-border text-industrial-text-secondary'}`}
            data-testid={`groove-${m.v}`}>
            <div className="font-bold">{m.l}</div><div className="text-[10px] opacity-70">{m.d}</div>
          </button>
        ))}
      </div>
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-3">
        <h3 className="text-sm font-heading font-bold text-orange-400">G75 {grMode === 'id' ? 'ID' : 'OD'} Grooving Parameters</h3>
        <div className="grid grid-cols-3 gap-2">
          <InputField label="Stock OD" value={stockDia} onChange={setStockDia} suffix='"' testId="g75-stock" />
          <InputField label={grMode === 'id' ? 'Groove ID' : 'Groove OD'} value={grooveDia} onChange={setGrooveDia} suffix='"' testId="g75-groove-dia" />
          <InputField label="Groove Width" value={grooveWidth} onChange={setGrooveWidth} suffix='"' testId="g75-width" />
          <InputField label="Z Position" value={grooveZ} onChange={setGrooveZ} suffix='"' testId="g75-z" />
          <InputField label="Peck Depth" value={peckDepth} onChange={setPeckDepth} suffix='"' testId="g75-peck" />
          <InputField label="Retract" value={retract} onChange={setRetract} suffix='"' testId="g75-retract" />
          <InputField label="Feed" value={feed} onChange={setFeed} suffix="IPR" testId="g75-feed" />
          <InputField label="Dwell (ms)" value={dwellMs} onChange={setDwellMs} suffix="ms" testId="g75-dwell" />
          <InputField label="SFM" value={sfm} onChange={setSfm} suffix="" testId="g75-sfm" />
          <InputField label="# Grooves" value={grooveCount} onChange={setGrooveCount} suffix="" testId="g75-count" />
          {grooveCount > 1 && <InputField label="Spacing" value={grooveSpacing} onChange={setGrooveSpacing} suffix='"' testId="g75-spacing" />}
        </div>
      </div>
      <GCodeOutput code={gcode} onCopy={onCopy} onDownload={onDownload} copied={copied} filename={`G75_${grMode}_groove.nc`} stockDia={stockDia} />
    </div>
  );
};

// ============ G76 Threading Generator ============
const G76Generator = ({ onCopy, onDownload, copied }) => {
  const [threadType, setThreadType] = useState('external');
  const [majorDia, setMajorDia] = useState(0.750);
  const [pitchDia, setPitchDia] = useState(0.7094);
  const [minorDia, setMinorDia] = useState(0.6823);
  const [tpi, setTpi] = useState(16);
  const [threadLength, setThreadLength] = useState(1.000);
  const [threadClass, setThreadClass] = useState('2A');
  const [springPasses, setSpringPasses] = useState(2);
  const [threadAngle, setThreadAngle] = useState(60);
  const [firstCutDepth, setFirstCutDepth] = useState(0.010);
  const [finishAllow, setFinishAllow] = useState(0.001);
  const [rpm, setRpm] = useState(800);

  const pitch = 1.0 / tpi;
  const threadHeight = ((majorDia - minorDia) / 2);

  const gcode = useMemo(() => {
    const lines = [];
    const endX = threadType === 'external' ? minorDia : majorDia;
    const htParam = Math.round(threadHeight * 10000);
    const fcParam = Math.round(firstCutDepth * 10000);
    const mcParam = Math.round(0.002 * 10000);
    lines.push('%');
    lines.push(`O0076 (G76 THREADING CYCLE - ${tpi} TPI)`);
    lines.push('(UNITS: INCH)');
    lines.push(`(THREAD: ${majorDia.toFixed(4)}-${tpi} ${threadType === 'external' ? 'EXT' : 'INT'} CLASS ${threadClass})`);
    lines.push(`(MAJOR: ${majorDia.toFixed(4)}", PITCH: ${pitchDia.toFixed(4)}", MINOR: ${minorDia.toFixed(4)}")`);
    lines.push(`(PITCH: ${pitch.toFixed(6)}" (1/${tpi}), HEIGHT: ${threadHeight.toFixed(4)}")`);
    lines.push('');
    lines.push('G20 G18 G40 G80 G99       (INCH, ZX PLANE, CANCEL)');
    lines.push('T0303 M08                  (THREADING TOOL, COOLANT ON)');
    lines.push(`G97 S${rpm} M03               (FIXED RPM - NOT CSS)`);
    lines.push(`G00 X${(majorDia + 0.200).toFixed(3)} Z0.200  (RAPID TO START)`);
    lines.push('');
    lines.push(`G76 P${String(springPasses).padStart(2,'0')}1060 Q${mcParam} R${finishAllow.toFixed(4)}`);
    lines.push(`G76 X${endX.toFixed(4)} Z${(-threadLength).toFixed(4)} P${htParam} Q${fcParam} F${pitch.toFixed(6)}`);
    lines.push('');
    lines.push(`G00 X${(majorDia + 0.500).toFixed(3)} Z1.000  (RETRACT SAFE)`);
    lines.push('M09'); lines.push('G28 U0 W0'); lines.push('M01');
    lines.push('');
    lines.push(`(VERIFY: GO/NOGO RING GAUGE ${tpi} TPI CLASS ${threadClass})`);
    lines.push(`(PITCH DIA: ${pitchDia.toFixed(4)}" NOM)`);
    lines.push('(G97 MUST BE ACTIVE FOR THREADING)');
    lines.push('M30'); lines.push('%');
    return lines.join('\n');
  }, [threadType, majorDia, pitchDia, minorDia, tpi, threadLength, threadClass, springPasses, firstCutDepth, finishAllow, rpm, pitch, threadHeight]);

  const presets = [
    { label: '1/4-20', major: 0.2500, pitch: 0.2175, minor: 0.1959, tpi: 20 },
    { label: '5/16-18', major: 0.3125, pitch: 0.2764, minor: 0.2524, tpi: 18 },
    { label: '3/8-16', major: 0.3750, pitch: 0.3344, minor: 0.3073, tpi: 16 },
    { label: '1/2-13', major: 0.5000, pitch: 0.4500, minor: 0.4167, tpi: 13 },
    { label: '5/8-11', major: 0.6250, pitch: 0.5660, minor: 0.5266, tpi: 11 },
    { label: '3/4-10', major: 0.7500, pitch: 0.6850, minor: 0.6417, tpi: 10 },
    { label: '3/4-16 UNF', major: 0.7500, pitch: 0.7094, minor: 0.6823, tpi: 16 },
    { label: '1-8', major: 1.0000, pitch: 0.9188, minor: 0.8647, tpi: 8 },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-2">
        <h3 className="text-sm font-heading font-bold text-orange-400">Thread Presets</h3>
        <div className="flex flex-wrap gap-1.5">
          {presets.map(p => (
            <button key={p.label} onClick={() => { setMajorDia(p.major); setPitchDia(p.pitch); setMinorDia(p.minor); setTpi(p.tpi); }}
              className="text-[11px] px-2 py-1 bg-industrial-bg border border-industrial-border rounded text-industrial-text-secondary hover:border-orange-400/50 hover:text-orange-400"
              data-testid={`preset-${p.label.replace(/[\s/]/g,'-')}`}>{p.label}</button>
          ))}
        </div>
      </div>
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2 mb-2">
          {['external','internal'].map(t => (
            <button key={t} onClick={() => setThreadType(t)} className={`py-2 rounded-lg border text-xs font-medium ${threadType === t ? 'bg-orange-500/20 border-orange-400 text-orange-400' : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary'}`} data-testid={`thread-${t}`}>{t === 'external' ? 'External' : 'Internal'} Thread</button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <InputField label="Major Dia" value={majorDia} onChange={setMajorDia} suffix='"' testId="g76-major" />
          <InputField label="Pitch Dia" value={pitchDia} onChange={setPitchDia} suffix='"' testId="g76-pitch" />
          <InputField label="Minor Dia" value={minorDia} onChange={setMinorDia} suffix='"' testId="g76-minor" />
          <InputField label="TPI" value={tpi} onChange={setTpi} suffix="" testId="g76-tpi" />
          <InputField label="Thread Len" value={threadLength} onChange={setThreadLength} suffix='"' testId="g76-len" />
          <InputField label="RPM (G97)" value={rpm} onChange={setRpm} suffix="" testId="g76-rpm" />
        </div>
      </div>
      <GCodeOutput code={gcode} onCopy={onCopy} onDownload={onDownload} copied={copied} filename="G76_thread.nc" stockDia={majorDia + 0.2} stages={[{ startDia: majorDia, endDia: majorDia, length: threadLength, entryR: 0, exitR: 0 }]} cycleType="g71" />
    </div>
  );
};

// ============ G81-G84 Drill/Tap Generator ============
const DrillTapGenerator = ({ onCopy, onDownload, copied }) => {
  const [cycleType, setCycleType] = useState('G83');
  const [drillDia, setDrillDia] = useState(0.500);
  const [zDepth, setZDepth] = useState(-1.500);
  const [rPlane, setRPlane] = useState(0.100);
  const [peckDepth, setPeckDepth] = useState(0.150);
  const [dwell, setDwell] = useState(500);
  const [feed, setFeed] = useState(0.005);
  const [rpm, setRpm] = useState(1200);
  const [tpi, setTpi] = useState(20);

  const gcode = useMemo(() => {
    const lines = [];
    const tapPitch = 1.0 / tpi;
    lines.push('%');
    lines.push(`O0083 (${cycleType} ${cycleType === 'G84' ? 'TAPPING' : 'DRILLING'} CYCLE)`);
    lines.push('(UNITS: INCH)');
    lines.push(`(${cycleType === 'G84' ? `TAP: ${tpi} TPI` : `DRILL DIA: ${drillDia.toFixed(3)}"` })`);
    lines.push(''); lines.push('G20 G18 G40 G80 G99       (INCH)');
    lines.push(cycleType === 'G84' ? 'T0404 M08                  (TAP TOOL)' : 'T0202 M08                  (DRILL TOOL)');
    lines.push(`G97 S${rpm} M03               (FIXED RPM)`);
    lines.push(`G00 X0.0000 Z${rPlane.toFixed(3)}       (RAPID TO CENTER)`);
    lines.push('');
    if (cycleType === 'G81') { lines.push(`G81 Z${zDepth.toFixed(4)} R${rPlane.toFixed(3)} F${feed.toFixed(4)}`); }
    else if (cycleType === 'G82') { lines.push(`G82 Z${zDepth.toFixed(4)} R${rPlane.toFixed(3)} P${dwell} F${feed.toFixed(4)}`); }
    else if (cycleType === 'G83') { lines.push(`G83 Z${zDepth.toFixed(4)} R${rPlane.toFixed(3)} Q${Math.round(peckDepth * 1000)} F${feed.toFixed(4)}`); lines.push(`(PECK: ${peckDepth.toFixed(3)}", ${Math.ceil(Math.abs(zDepth) / peckDepth)} PECKS)`); }
    else if (cycleType === 'G84') { lines.push(`M29 S${rpm}`); lines.push(`G84 Z${zDepth.toFixed(4)} R${rPlane.toFixed(3)} F${tapPitch.toFixed(6)}`); }
    lines.push(''); lines.push('G80'); lines.push('G00 Z1.000'); lines.push('M09'); lines.push('G28 U0 W0'); lines.push('M01'); lines.push('M30'); lines.push('%');
    return lines.join('\n');
  }, [cycleType, drillDia, zDepth, rPlane, peckDepth, dwell, feed, rpm, tpi]);

  return (
    <div className="space-y-4">
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3">
        <div className="grid grid-cols-2 gap-1.5">
          {[{id:'G81',l:'G81 Drill'},{id:'G82',l:'G82 Drill+Dwell'},{id:'G83',l:'G83 Peck Drill'},{id:'G84',l:'G84 Tap'}].map(c => (
            <button key={c.id} onClick={() => setCycleType(c.id)} className={`p-2 rounded-lg border text-xs font-bold ${cycleType === c.id ? 'bg-orange-500/20 border-orange-400 text-orange-400' : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary'}`} data-testid={`cycle-${c.id}`}>{c.l}</button>
          ))}
        </div>
      </div>
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3">
        <div className="grid grid-cols-3 gap-2">
          {cycleType !== 'G84' && <InputField label="Drill Dia" value={drillDia} onChange={setDrillDia} suffix='"' testId="drill-dia" />}
          <InputField label="Z Depth" value={zDepth} onChange={setZDepth} suffix='"' testId="z-depth" />
          <InputField label="R-Plane" value={rPlane} onChange={setRPlane} suffix='"' testId="r-plane" />
          <InputField label="RPM" value={rpm} onChange={setRpm} suffix="" testId="drill-rpm" />
          {['G81','G82','G83'].includes(cycleType) && <InputField label="Feed" value={feed} onChange={setFeed} suffix="IPR" testId="drill-feed" />}
          {cycleType === 'G83' && <InputField label="Peck" value={peckDepth} onChange={setPeckDepth} suffix='"' testId="peck-depth" />}
          {cycleType === 'G82' && <InputField label="Dwell" value={dwell} onChange={setDwell} suffix="ms" testId="dwell" />}
          {cycleType === 'G84' && <InputField label="TPI" value={tpi} onChange={setTpi} suffix="" testId="tap-tpi" />}
        </div>
      </div>
      <GCodeOutput code={gcode} onCopy={onCopy} onDownload={onDownload} copied={copied} filename={`${cycleType}_cycle.nc`} stockDia={drillDia * 3} />
    </div>
  );
};

// ============ CYCLE CHAINING BUILDER ============
const CycleChainBuilder = ({ onCopy, onDownload, copied }) => {
  const [programNum, setProgramNum] = useState(1001);
  const [partName, setPartName] = useState('TURNED PART');
  const [stockDia, setStockDia] = useState(2.0);
  const [stockLen, setStockLen] = useState(3.0);
  const [material, setMaterial] = useState('4140 STEEL');
  const [maxRpm, setMaxRpm] = useState(3000);
  
  // Chain operations - each is a toggle with minimal params
  const [ops, setOps] = useState({
    face: { enabled: true, sfm: 400, feed: 0.008, depth: 0.060 },
    odRough: { enabled: true, sfm: 400, roughFeed: 0.010, finFeed: 0.004, doc: 0.080, finX: 0.020, finZ: 0.005, stages: [
      { startDia: 1.500, endDia: 1.500, length: 1.500 },
      { startDia: 1.000, endDia: 1.000, length: 1.000 },
    ]},
    groove: { enabled: false, sfm: 300, feed: 0.003, dia: 0.900, width: 0.125, zPos: -1.500, dwell: 300 },
    thread: { enabled: false, tpi: 16, majorDia: 1.000, pitchDia: 0.9594, minorDia: 0.9323, length: 0.750, rpm: 800 },
    drill: { enabled: false, dia: 0.500, depth: -2.000, peck: 0.150, feed: 0.005, rpm: 1200 },
    bore: { enabled: false, sfm: 350, roughFeed: 0.008, finFeed: 0.003, doc: 0.060, finX: 0.015, stages: [
      { startDia: 0.750, endDia: 0.750, length: 1.500 },
    ]},
    partoff: { enabled: false, sfm: 250, feed: 0.002, zPos: -2.600, width: 0.125 },
  });

  const toggleOp = (key) => setOps({ ...ops, [key]: { ...ops[key], enabled: !ops[key].enabled } });
  const updateOp = (key, field, val) => setOps({ ...ops, [key]: { ...ops[key], [field]: parseFloat(val) || val } });

  const gcode = useMemo(() => {
    const L = [];
    const o = String(programNum).padStart(4, '0');
    let toolNum = 1;

    L.push('%');
    L.push(`O${o} (${partName})`);
    L.push(`(MATERIAL: ${material})`);
    L.push(`(STOCK: DIA ${stockDia.toFixed(3)}" x ${stockLen.toFixed(3)}" OAL)`);
    L.push('(UNITS: INCH)');
    
    // Build tool list
    const tools = [];
    if (ops.face.enabled || ops.odRough.enabled) tools.push(`( T0${toolNum}0${toolNum} - OD TURNING / FACING TOOL)`);
    if (ops.groove.enabled) tools.push(`( T0${toolNum+1}0${toolNum+1} - OD GROOVE TOOL ${ops.groove.width.toFixed(3)}" WIDE)`);
    if (ops.thread.enabled) tools.push(`( T0${toolNum+2}0${toolNum+2} - THREADING TOOL ${ops.thread.tpi} TPI)`);
    if (ops.drill.enabled) tools.push(`( T0${toolNum+3}0${toolNum+3} - DRILL DIA ${ops.drill.dia.toFixed(3)}")`);
    if (ops.bore.enabled) tools.push(`( T0${toolNum+4}0${toolNum+4} - BORING BAR)`);
    if (ops.partoff.enabled) tools.push(`( T0${toolNum+5}0${toolNum+5} - PART-OFF TOOL ${ops.partoff.width.toFixed(3)}" WIDE)`);
    L.push('(TOOL LIST:)');
    tools.forEach(t => L.push(t));
    L.push('(PART ZERO: Z0 = FACE, X0 = CENTERLINE)');
    L.push('');
    L.push('G20 G18 G40 G80 G99       (INCH, ZX PLANE, CANCEL)');
    
    let tn = 1;

    // === FACE ===
    if (ops.face.enabled) {
      L.push('');
      L.push('(==================================================)');
      L.push(`(  T0${tn}0${tn} - FACING OPERATION                        )`);
      L.push('(==================================================)');
      L.push(`G28 U0 W0                  (HOME)`);
      L.push(`T0${tn}0${tn} M08                  (FACING TOOL, COOLANT)`);
      L.push(`G50 S${maxRpm}`);
      L.push(`G96 S${ops.face.sfm} M03`);
      L.push(`G00 X${(stockDia + 0.200).toFixed(3)} Z0.010`);
      L.push(`G01 X-0.020 F${ops.face.feed.toFixed(4)}  (FACE TO CENTER)`);
      L.push(`G00 Z0.100`);
      L.push('M01                        (OPTIONAL STOP)');
    }

    // === OD ROUGH/FINISH ===
    if (ops.odRough.enabled) {
      const stg = ops.odRough.stages;
      L.push('');
      L.push('(==================================================)');
      L.push(`(  T0${tn}0${tn} - G71/G70 OD ROUGH & FINISH              )`);
      L.push('(==================================================)');
      if (!ops.face.enabled) { L.push(`G28 U0 W0`); L.push(`T0${tn}0${tn} M08`); L.push(`G50 S${maxRpm}`); L.push(`G96 S${ops.odRough.sfm} M03`); }
      L.push(`G00 X${(stockDia + 0.200).toFixed(3)} Z0.100`);
      
      let nB = 100, zP = 0;
      const pLines = [];
      pLines.push(`N${nB} G00 X${stg[0].startDia.toFixed(4)}`);
      nB += 10; pLines.push(`N${nB} G01 Z0.0000 F${ops.odRough.finFeed.toFixed(4)}`);
      for (const s of stg) {
        const eZ = zP - s.length;
        nB += 10;
        if (Math.abs(s.startDia - s.endDia) > 0.001) pLines.push(`N${nB} G01 X${s.endDia.toFixed(4)} Z${eZ.toFixed(4)}`);
        else pLines.push(`N${nB} G01 Z${eZ.toFixed(4)}              (DIA ${s.startDia.toFixed(3)}" x ${s.length.toFixed(3)}")`);
        zP = eZ;
      }
      nB += 10; pLines.push(`N${nB} G01 X${(stockDia + 0.050).toFixed(4)}`);
      
      L.push(`G71 U${ops.odRough.doc.toFixed(3)} R0.030`);
      L.push(`G71 P100 Q${nB} U${ops.odRough.finX.toFixed(3)} W${ops.odRough.finZ.toFixed(3)} F${ops.odRough.roughFeed.toFixed(4)}`);
      pLines.forEach(pl => L.push(pl));
      L.push(`G70 P100 Q${nB}              (FINISH PASS)`);
      L.push(`G00 X${(stockDia + 0.500).toFixed(3)} Z1.000`);
      L.push('M09'); L.push('M01');
    }
    if (ops.face.enabled || ops.odRough.enabled) tn++;

    // === GROOVE ===
    if (ops.groove.enabled) {
      L.push('');
      L.push('(==================================================)');
      L.push(`(  T0${tn}0${tn} - G75 OD GROOVING                        )`);
      L.push('(==================================================)');
      L.push('G28 U0 W0');
      L.push(`T0${tn}0${tn} M08                  (GROOVE TOOL)`);
      L.push(`G50 S${maxRpm}`);
      L.push(`G96 S${ops.groove.sfm} M03`);
      L.push(`G00 X${(stockDia + 0.200).toFixed(3)} Z${ops.groove.zPos.toFixed(3)}`);
      L.push(`G75 R0.010`);
      L.push(`G75 X${ops.groove.dia.toFixed(4)} Z${(ops.groove.zPos - ops.groove.width).toFixed(4)} P${Math.round(0.030 * 1000)} Q0 F${ops.groove.feed.toFixed(4)}`);
      L.push(`G04 P${ops.groove.dwell}               (DWELL AT BOTTOM)`);
      L.push(`G00 X${(stockDia + 0.200).toFixed(3)}`);
      L.push('M09'); L.push('M01');
      tn++;
    }

    // === THREAD ===
    if (ops.thread.enabled) {
      const pitch = 1.0 / ops.thread.tpi;
      const tH = (ops.thread.majorDia - ops.thread.minorDia) / 2;
      L.push('');
      L.push('(==================================================)');
      L.push(`(  T0${tn}0${tn} - G76 THREADING ${ops.thread.tpi} TPI              )`);
      L.push('(==================================================)');
      L.push('G28 U0 W0');
      L.push(`T0${tn}0${tn} M08                  (THREAD TOOL)`);
      L.push(`G97 S${ops.thread.rpm} M03         (FIXED RPM FOR THREADING)`);
      L.push(`G00 X${(ops.thread.majorDia + 0.200).toFixed(3)} Z0.200`);
      L.push(`G76 P021060 Q${Math.round(0.002 * 10000)} R0.0010`);
      L.push(`G76 X${ops.thread.minorDia.toFixed(4)} Z${(-ops.thread.length).toFixed(4)} P${Math.round(tH * 10000)} Q${Math.round(0.010 * 10000)} F${pitch.toFixed(6)}`);
      L.push(`G00 X${(ops.thread.majorDia + 0.500).toFixed(3)} Z1.000`);
      L.push('M09'); L.push('M01');
      tn++;
    }

    // === DRILL ===
    if (ops.drill.enabled) {
      L.push('');
      L.push('(==================================================)');
      L.push(`(  T0${tn}0${tn} - G83 PECK DRILLING                      )`);
      L.push('(==================================================)');
      L.push('G28 U0 W0');
      L.push(`T0${tn}0${tn} M08                  (DRILL)`);
      L.push(`G97 S${ops.drill.rpm} M03`);
      L.push('G00 X0.000 Z0.100');
      L.push(`G83 Z${ops.drill.depth.toFixed(4)} R0.100 Q${Math.round(ops.drill.peck * 1000)} F${ops.drill.feed.toFixed(4)}`);
      L.push('G80'); L.push('G00 Z1.000');
      L.push('M09'); L.push('M01');
      tn++;
    }

    // === BORE ===
    if (ops.bore.enabled) {
      const bStg = ops.bore.stages;
      L.push('');
      L.push('(==================================================)');
      L.push(`(  T0${tn}0${tn} - G71/G70 ID BORING                      )`);
      L.push('(==================================================)');
      L.push('G28 U0 W0');
      L.push(`T0${tn}0${tn} M08                  (BORING BAR)`);
      L.push(`G50 S${maxRpm}`);
      L.push(`G96 S${ops.bore.sfm} M03`);
      L.push('G00 X0.000 Z0.100');
      let bnB = 200, bzP = 0;
      const bpLines = [];
      bpLines.push(`N${bnB} G00 X${bStg[0].startDia.toFixed(4)}`);
      bnB += 10; bpLines.push(`N${bnB} G01 Z0.0000 F${ops.bore.finFeed.toFixed(4)}`);
      for (const s of bStg) {
        const eZ = bzP - s.length;
        bnB += 10;
        bpLines.push(`N${bnB} G01 Z${eZ.toFixed(4)}              (BORE DIA ${s.startDia.toFixed(3)}")`);
        bzP = eZ;
      }
      bnB += 10; bpLines.push(`N${bnB} G01 X0.0000`);
      L.push(`G71 U${ops.bore.doc.toFixed(3)} R0.020`);
      L.push(`G71 P200 Q${bnB} U-${ops.bore.finX.toFixed(3)} W0.005 F${ops.bore.roughFeed.toFixed(4)}`);
      bpLines.forEach(bl => L.push(bl));
      L.push(`G70 P200 Q${bnB}`);
      L.push('G00 X0.000 Z1.000');
      L.push('M09'); L.push('M01');
      tn++;
    }

    // === PART OFF ===
    if (ops.partoff.enabled) {
      L.push('');
      L.push('(==================================================)');
      L.push(`(  T0${tn}0${tn} - PART-OFF                               )`);
      L.push('(==================================================)');
      L.push('G28 U0 W0');
      L.push(`T0${tn}0${tn} M08                  (PART-OFF TOOL)`);
      L.push(`G50 S${maxRpm}`);
      L.push(`G96 S${ops.partoff.sfm} M03`);
      L.push(`G00 X${(stockDia + 0.100).toFixed(3)} Z${ops.partoff.zPos.toFixed(3)}`);
      L.push(`G01 X0.200 F${ops.partoff.feed.toFixed(4)}`);
      L.push(`    X0.050 F${(ops.partoff.feed * 0.5).toFixed(4)}  (SLOW NEAR CENTER)`);
      L.push(`    X-0.020 F${(ops.partoff.feed * 0.4).toFixed(4)} (FEED THROUGH)`);
      L.push('G04 P500                   (DWELL)');
      L.push(`G00 X${(stockDia + 0.500).toFixed(3)}`);
      L.push('M09'); L.push('M01');
      tn++;
    }

    L.push('');
    L.push('(==================================================)');
    L.push('G28 U0 W0                  (FINAL HOME)');
    L.push('M30                        (PROGRAM END)');
    L.push('%');
    return L.join('\n');
  }, [programNum, partName, stockDia, stockLen, material, maxRpm, ops]);

  const opDefs = [
    { key: 'face', label: 'Face', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    { key: 'odRough', label: 'OD Rough/Finish (G71/G70)', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { key: 'groove', label: 'Groove (G75)', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { key: 'thread', label: 'Thread (G76)', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { key: 'drill', label: 'Drill (G83)', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
    { key: 'bore', label: 'ID Bore (G71 ID)', color: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
    { key: 'partoff', label: 'Part-Off', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-3">
        <h3 className="text-sm font-heading font-bold text-orange-400 flex items-center gap-2"><Link className="w-4 h-4" />Program Setup</h3>
        <div className="grid grid-cols-3 gap-2">
          <InputField label="O-Number" value={programNum} onChange={setProgramNum} testId="chain-onum" />
          <InputField label="Part Name" value={partName} onChange={setPartName} isText testId="chain-name" />
          <InputField label="Material" value={material} onChange={setMaterial} isText testId="chain-mat" />
          <InputField label="Stock OD" value={stockDia} onChange={setStockDia} suffix='"' testId="chain-stock" />
          <InputField label="Stock Len" value={stockLen} onChange={setStockLen} suffix='"' testId="chain-len" />
          <InputField label="Max RPM" value={maxRpm} onChange={setMaxRpm} testId="chain-rpm" />
        </div>
      </div>

      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-2">
        <h3 className="text-sm font-heading font-bold text-orange-400">Operations (toggle to include)</h3>
        <div className="space-y-1.5">
          {opDefs.map(od => (
            <button key={od.key} onClick={() => toggleOp(od.key)}
              className={`w-full text-left px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                ops[od.key].enabled ? od.color : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary opacity-50'
              }`} data-testid={`chain-toggle-${od.key}`}>
              <span className="mr-2">{ops[od.key].enabled ? '\u2611' : '\u2610'}</span>
              {od.label}
            </button>
          ))}
        </div>
      </div>

      <GCodeOutput code={gcode} onCopy={onCopy} onDownload={onDownload} copied={copied} filename={`O${String(programNum).padStart(4,'0')}_chained.nc`} stockDia={stockDia} />
    </div>
  );
};

// ============ Shared Components ============
const InputField = ({ label, value, onChange, suffix, mini, isText, testId }) => (
  <div>
    <label className={`text-industrial-text-secondary block mb-1 ${mini ? 'text-[10px]' : 'text-xs'}`}>{label}</label>
    <div className="flex items-center gap-1">
      <input type={isText ? 'text' : 'number'} step={isText ? undefined : '0.001'} value={value}
        onChange={e => onChange(isText ? e.target.value : parseFloat(e.target.value) || 0)}
        className={`w-full bg-industrial-bg border border-industrial-border rounded text-industrial-text font-mono ${mini ? 'p-1.5 text-xs' : 'p-2 text-sm'}`}
        data-testid={testId} />
      {suffix && <span className={`text-industrial-text-secondary flex-shrink-0 ${mini ? 'text-[10px]' : 'text-xs'}`}>{suffix}</span>}
    </div>
  </div>
);

const GCodeOutput = ({ code, onCopy, onDownload, copied, filename, stockDia, stages, cycleType, facingParams, depthOfCut, finishAllowX }) => {
  const [show3D, setShow3D] = useState(false);
  const totalLength = stages ? stages.reduce((sum, s) => sum + s.length, 0) : 2.0;
  return (
    <div className="space-y-3">
      {(stages || facingParams) && (
        <ProfileVisualizer stages={stages || []} stockDia={stockDia || 2.0} cycleType={cycleType || 'g71'} facingParams={facingParams} depthOfCut={depthOfCut || 0} finishAllowX={finishAllowX || 0} />
      )}
      <div className="flex items-center gap-2">
        <button onClick={() => setShow3D(!show3D)} className={`flex-1 py-2.5 rounded-lg border text-xs font-medium flex items-center justify-center gap-2 transition-all ${show3D ? 'bg-blue-500/20 border-blue-400 text-blue-400' : 'bg-industrial-surface border-industrial-border text-industrial-text-secondary hover:border-blue-400/50'}`} data-testid="toggle-3d">
          <Box className="w-4 h-4" />{show3D ? 'Hide 3D Simulation' : 'Show 3D Simulation'}
        </button>
      </div>
      {show3D && code && (
        <DynamicToolpathViewer gcode={code} stockDia={stockDia || 2.0} totalLength={totalLength} />
      )}
      <div className="bg-industrial-surface border border-orange-500/30 rounded-xl overflow-hidden" data-testid="gcode-output">
        <div className="flex items-center justify-between px-3 py-2 bg-orange-500/10 border-b border-orange-500/20">
          <span className="text-xs font-heading font-bold text-orange-400">Generated G-Code ({code.split('\n').length} lines)</span>
          <div className="flex items-center gap-2">
            <button onClick={() => onCopy(code)} className="text-xs text-industrial-text-secondary hover:text-orange-400 flex items-center gap-1" data-testid="copy-gcode">
              {copied ? <CheckCircle className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}{copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={() => onDownload(code, filename)} className="text-xs text-industrial-text-secondary hover:text-orange-400 flex items-center gap-1" data-testid="download-gcode">
              <Download className="w-3 h-3" /> .nc
            </button>
          </div>
        </div>
        <pre className="p-3 font-mono text-xs text-industrial-text overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre leading-relaxed">{code}</pre>
      </div>
    </div>
  );
};

// Dynamic 3D viewer - loads Three.js only when user clicks "Show 3D"
const DynamicToolpathViewer = ({ gcode, stockDia, totalLength }) => {
  const [Comp, setComp] = React.useState(null);
  React.useEffect(() => {
    let mounted = true;
    import(/* webpackChunkName: "toolpath3d" */ '@/components/ToolpathViewer3D').then(mod => {
      if (mounted) setComp(() => mod.default);
    });
    return () => { mounted = false; };
  }, []);
  if (!Comp) return <div className="h-[350px] flex items-center justify-center bg-industrial-bg rounded-lg"><div className="w-6 h-6 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" /></div>;
  return <div className="rounded-xl overflow-hidden border border-blue-500/30"><Comp gcode={gcode} machineType="turning" stockDimensions={{ diameter: stockDia, length: totalLength * 1.3 }} height={350} /></div>;
};

export default CycleGeneratorPage;
